import { useState } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { deliverableLabel, overdueKind, usageRightsDetail } from "@talentkontor/domain";
import { api } from "../../data";
import { Dialog, PillView } from "../../components/ui";
import { loadOwnChatFiles, type CDeal, type CDeliverable, type CPeriod, type CreatorBase } from "./data";
import { reportDeliverableLive, submitDeliverableDraft } from "./actions";
import { pill, platformLabel } from "./model";
import { dateOf, useLang, useT, type Lang } from "./sprache";
import { useCreatorAction, useCreatorContext } from "./ui";
import { BriefingAnsicht } from "../oeffentlich/MarkeBausteine";
import { KennzahlenCreatorDialog, beiDerMarke, beitragsBriefing, kennzahlenAb, kennzahlenText, useCreatorMarke } from "./marke";
import { EigeneDateienListe, FassungDialog, creatorUploadFehler, useEigeneDateien } from "./ContentAblage";
import { UploadFeld } from "../../components/medien/UploadFeld";

/**
 * Beiträge einer Kooperation im Creator-Portal (0068): was bis wann als Entwurf fällig ist, was freigegeben ist und gepostet
 * werden kann, was live ist. Der Creator reicht Entwürfe ein (Dateien aus seinem Chat oder ein Link) und meldet „live“ mit
 * Link und Datum; alles andere pflegt die Agentur.
 */

const STAND: Record<CDeliverable["status"], { de: string; en: string; tone: "neutral" | "info" | "warning" | "success" }> = {
  planned: { de: "Geplant", en: "Planned", tone: "neutral" },
  draft_submitted: { de: "Entwurf eingereicht", en: "Draft submitted", tone: "info" },
  changes_requested: { de: "Korrektur angefragt", en: "Changes requested", tone: "warning" },
  approved: { de: "Freigegeben · bitte posten", en: "Approved · please post", tone: "warning" },
  live: { de: "Live", en: "Live", tone: "success" },
  cancelled: { de: "Entfallen", en: "Cancelled", tone: "neutral" },
};

/** Ohne Vorabfreigabe (0095, PRAXIS-R3-5) meldet der Creator den Beitrag direkt live */
export function darfLiveMelden(x: Pick<CDeliverable, "status" | "approval_required">): boolean {
  return x.status === "approved" || (x.approval_required === false && ["planned", "draft_submitted", "changes_requested"].includes(x.status));
}

export function creatorDeliverablePill(x: Pick<CDeliverable, "status" | "draft_due" | "publish_on" | "approval_required">, today: string, lang: Lang, atBrand = false) {
  const k = overdueKind(x, today);
  // CREATOR-R2-1: Posten ist nur nach der Freigabe überfällig; davor fehlt der Entwurf (Posttermin verstrichen).
  // PRAXIS-R3-5: ohne Vorabfreigabe gibt es keinen Entwurf, der fehlen könnte
  if (k === "publish" && darfLiveMelden(x)) return pill(lang === "en" ? "Post overdue" : "Posten überfällig", "error");
  if (k === "publish" && x.status === "draft_submitted") return pill(lang === "en" ? "Posting date passed · awaiting approval" : "Posttermin verstrichen · Freigabe offen", "warning");
  if (k === "publish" && !x.draft_due) return pill(lang === "en" ? "Posting date passed · draft missing" : "Posttermin verstrichen · Entwurf fehlt", "error");
  if (k) return pill(lang === "en" ? "Draft overdue" : "Entwurf überfällig", "error");
  // 0073: Der Entwurf liegt per Freigabe-Link bei der Marke
  if (atBrand && x.status === "draft_submitted") return pill(lang === "en" ? "With the brand for approval" : "Bei der Marke zur Freigabe", "info");
  const s = STAND[x.status];
  return pill(lang === "en" ? s.en : s.de, s.tone);
}

type Offen = null | { art: "entwurf"; id: string } | { art: "live"; id: string } | { art: "kennzahlen"; id: string } | { art: "fassung"; id: string };

/** Beiträge, die der Abschnitt „Deine Beiträge“ im Deal zeigt: die des gewählten Monats und die ohne Monat, sonst alle. */
export function gezeigteBeitraege(b: CreatorBase, deal: CDeal, periods: readonly CPeriod[], period: CPeriod | null): CDeliverable[] {
  const alle = b.deliverables.filter((x) => x.deal_id === deal.id);
  const ohneMonat = alle.filter((x) => !x.period_id || !periods.some((p) => p.id === x.period_id));
  return period ? [...alle.filter((x) => x.period_id === period.id), ...ohneMonat] : alle;
}

export function DealBeitraege({ b, deal, periods, period, today }: { b: CreatorBase; deal: CDeal; periods: readonly CPeriod[]; period: CPeriod | null; today: string }) {
  const { lang } = useCreatorContext();
  const t = useT();
  const [offen, setOffen] = useState<Offen>(null);
  const alle = b.deliverables.filter((x) => x.deal_id === deal.id);
  const gezeigt = gezeigteBeitraege(b, deal, periods, period);
  // Hook vor dem frühen Ausstieg: sonst wechselt die Hook-Anzahl, sobald ein Monat ohne Beitrag gewählt wird
  const marke = useCreatorMarke(deal.id, alle.map((x) => x.id)).data;
  if (!gezeigt.length) return null;
  const name = (x: CDeliverable) => deliverableLabel(x, (k) => platformLabel(k, lang, b.platformNames), lang);
  const fmt = (d: string) => dateOf(d, lang, b.org.timezone);
  const gesperrt = ["blocked", "cancelled"].includes(deal.status);
  const letzteRueckmeldung = (id: string) => b.deliverableSubmissions.find((s) => s.deliverable_id === id && s.status === "changes_requested");
  /** neueste Fassung mit Dateien der Content-Ablage (0155) */
  const letzteMitDateien = (id: string) => b.deliverableSubmissions.filter((s) => s.deliverable_id === id && (s.media_ids?.length ?? 0) > 0)
    .sort((a, c) => c.version - a.version)[0];
  const gewaehlt = offen ? alle.find((x) => x.id === offen.id) : undefined;
  return (
    <section className="panel stack" id="beitraege" aria-labelledby="beitraege-titel">
      <h3 id="beitraege-titel">{t("Deine Beiträge", "Your content")}</h3>
      <p className="source-note">{t("Den Entwurf lädst du hier hoch (Video, Bild oder PDF, am Handy aus der Kamerarolle) oder schickst einen Link. Ist der Beitrag online, meldest du ihn mit Link und Datum.",
        "Upload your draft here (video, image or PDF, from the camera roll on your phone) or send a link. Once the post is online, report it with the link and date.")}</p>
      {gezeigt.map((x) => {
        const rueck = x.status === "changes_requested" ? letzteRueckmeldung(x.id) : undefined;
        const zeilen = x.status === "live" && x.published_on
          ? [t(`live seit ${fmt(x.published_on)}`, `live since ${fmt(x.published_on)}`),
             x.usage_rights_until ? t(`Nutzungsrechte bis ${fmt(x.usage_rights_until)}`, `usage rights until ${fmt(x.usage_rights_until)}`) : null]
          : [x.draft_due ? t(`Entwurf bis ${fmt(x.draft_due)}`, `draft by ${fmt(x.draft_due)}`) : null,
             x.publish_on ? t(`posten am ${fmt(x.publish_on)}`, `post on ${fmt(x.publish_on)}`) : null,
             !x.period_id && period ? t("für die ganze Kooperation", "for the whole collaboration") : null];
        const text = zeilen.filter(Boolean).join(" · ") || t("Termin folgt von deiner Agentur", "Date to follow from your agency");
        return (
          <div key={x.id} className="task tk-beitrag">
            <div>
              <strong>{name(x)}</strong>
              <p>{text}</p>
              {rueck?.feedback && <div className="revision-note">{t("Korrekturwunsch", "Change request")}: {rueck.feedback}</div>}
              {x.status === "changes_requested" && !rueck?.feedback && <p className="source-note">{t("Deine Agentur schickt dir die Details zur Änderung.", "Your agency will send you the details of the change.")}</p>}
              {x.status === "live" && (() => {
                const m = marke?.metrics.find((y) => y.deliverable_id === x.id);
                const txt = kennzahlenText(m, lang);
                const ab = kennzahlenAb(marke, x.id);
                return <p className="source-note">{txt ? t(`Kennzahlen: ${txt}`, `Metrics: ${txt}`) : m ? t("Screenshots hochgeladen.", "Screenshots uploaded.")
                  : ab ? t(`Kennzahlen bitte ab ${fmt(ab)} hochladen.`, `Please upload metrics from ${fmt(ab)}.`) : null}</p>;
              })()}
              {marke && beitragsBriefing(marke, x.id) && (
                <details>
                  <summary>{t("Briefing zu diesem Beitrag", "Briefing for this post")}</summary>
                  <BriefingAnsicht briefing={beitragsBriefing(marke, x.id)!} lang={lang} />
                </details>
              )}
            </div>
            <div className="row">
              <PillView pill={creatorDeliverablePill(x, today, lang, beiDerMarke(marke, x.id))} />
              {letzteMitDateien(x.id) && (
                <button type="button" className={x.status === "changes_requested" ? "primary" : ""} onClick={() => setOffen({ art: "fassung", id: x.id })}>
                  {x.status === "changes_requested" ? t("Korrektur ansehen", "View changes") : t(`Version ${letzteMitDateien(x.id)!.version} ansehen`, `View version ${letzteMitDateien(x.id)!.version}`)}
                </button>
              )}
              {!gesperrt && (x.status === "planned" || x.status === "changes_requested") && (
                <button className={x.approval_required === false ? "" : "primary"} onClick={() => setOffen({ art: "entwurf", id: x.id })}>{x.status === "changes_requested" ? t("Neuen Entwurf einreichen", "Submit new draft") : t("Entwurf einreichen", "Submit draft")}</button>
              )}
              {!gesperrt && darfLiveMelden(x) && <button className={x.status === "approved" || x.approval_required === false ? "primary" : ""} onClick={() => setOffen({ art: "live", id: x.id })}>{t("Live melden", "Report live")}</button>}
              {x.status === "live" && x.live_url && <a className="btn" href={x.live_url} target="_blank" rel="noopener noreferrer">{t("Beitrag öffnen", "Open post")}</a>}
              {!gesperrt && x.status === "live" && <button onClick={() => setOffen({ art: "live", id: x.id })}>{t("Link ändern", "Change link")}</button>}
              {!gesperrt && x.status === "live" && marke && (
                <button className={marke.metrics.some((m) => m.deliverable_id === x.id) ? "" : "primary"} onClick={() => setOffen({ art: "kennzahlen", id: x.id })}>
                  {marke.metrics.some((m) => m.deliverable_id === x.id) ? t("Kennzahlen ändern", "Edit metrics") : t("Kennzahlen hochladen", "Upload metrics")}
                </button>
              )}
            </div>
          </div>
        );
      })}
      {offen?.art === "entwurf" && gewaehlt && <EntwurfDialog beitrag={gewaehlt} name={name(gewaehlt)} onClose={() => setOffen(null)} />}
      {offen?.art === "fassung" && gewaehlt && letzteMitDateien(gewaehlt.id) && (
        <FassungDialog beitrag={gewaehlt} name={t(`${name(gewaehlt)} · Version ${letzteMitDateien(gewaehlt.id)!.version}`, `${name(gewaehlt)} · version ${letzteMitDateien(gewaehlt.id)!.version}`)}
          mediaIds={letzteMitDateien(gewaehlt.id)!.media_ids ?? []} feedback={letzteMitDateien(gewaehlt.id)!.status === "changes_requested" ? letzteMitDateien(gewaehlt.id)!.feedback : null}
          onClose={() => setOffen(null)} />
      )}
      {offen?.art === "live" && gewaehlt && <LiveDialog beitrag={gewaehlt} name={name(gewaehlt)} today={today} onClose={() => setOffen(null)} />}
      {offen?.art === "kennzahlen" && gewaehlt && marke && (
        <KennzahlenCreatorDialog beitrag={gewaehlt} name={name(gewaehlt)} metrics={marke.metrics.find((m) => m.deliverable_id === gewaehlt.id)}
          dateien={marke.metricsFiles.filter((f) => f.owner_id === gewaehlt.id)} onClose={() => setOffen(null)} />
      )}
    </section>
  );
}

function EntwurfDialog({ beitrag, name, onClose }: { beitrag: CDeliverable; name: string; onClose: () => void }) {
  const { scope, lang } = useCreatorContext();
  const t = useT();
  const { busy, error, run } = useCreatorAction();
  const [text, setText] = useState("");
  const [wahl, setWahl] = useState<Set<string>>(new Set());
  // Abgewählte statt gewählte Dateien merken: was später fertig wird, ist automatisch dabei
  const [abgewaehlt, setAbgewaehlt] = useState<Set<string>>(new Set());
  const [laufend, setLaufend] = useState(0);
  const eigene = useEigeneDateien(beitrag.id);
  const qc = useQueryClient();
  const medien = new Set((eigene.data ?? []).map((m) => m.id).filter((id) => !abgewaehlt.has(id)));
  const dateien = useQuery({ queryKey: ["creator", scope.talentId, "chat-dateien"], queryFn: () => loadOwnChatFiles(api, scope) });
  const leer = !text.trim() && wahl.size === 0 && medien.size === 0;
  return (
    <Dialog open onClose={onClose} title={t(`Entwurf einreichen · ${name}`, `Submit draft · ${name}`)}>
      <form className="stack" onSubmit={(e) => {
        e.preventDefault();
        if (leer || laufend > 0) return;
        void run("entwurf", () => submitDeliverableDraft(api, { deliverableId: beitrag.id, caption: text, attachmentIds: [...wahl], mediaIds: [...medien] }),
          t("Entwurf eingereicht. Deine Agentur prüft ihn.", "Draft submitted. Your agency will review it.")).then((r) => r && onClose());
      }}>
        <UploadFeld ziel={{ deal: beitrag.deal_id, deliverable: beitrag.id }} t={t} lang={lang} label={t("Video oder Bild hochladen", "Upload video or image")}
          fehlerText={(e) => creatorUploadFehler(e, lang)} onLaufend={setLaufend}
          onFertig={() => void qc.invalidateQueries({ queryKey: ["creator", "media_files", beitrag.id] })} />
        <EigeneDateienListe deliverableId={beitrag.id} abgewaehlt={abgewaehlt} setAbgewaehlt={setAbgewaehlt} />
        <p>{t("Oder wähle Dateien, die du deiner Agentur im Chat geschickt hast, bzw. füge einen Link zum Entwurf ein (z. B. aus deiner Cloud).",
          "Or choose files you sent to your agency in the chat, or paste a link to the draft (e.g. from your cloud storage).")}</p>
        {dateien.isPending && <p className="muted">{t("Dateien werden geladen …", "Loading files …")}</p>}
        {dateien.data && dateien.data.length === 0 && (
          <p className="muted">{t("Du hast noch keine Datei im Chat geschickt.", "You have not sent a file in the chat yet.")} <Link to="/creator/nachrichten">{t("Zum Chat", "Go to chat")}</Link></p>
        )}
        {(dateien.data ?? []).slice(0, 20).map((a) => (
          <label key={a.id} className="tk-check">
            <input type="checkbox" checked={wahl.has(a.id)} onChange={(e) => setWahl((s) => {
              const n = new Set(s);
              if (e.target.checked) n.add(a.id);
              else n.delete(a.id);
              return n;
            })} />
            <span>{a.file_name} · {dateOf(a.created_at.slice(0, 10), lang)}</span>
          </label>
        ))}
        <label>{t("Link oder Hinweis zum Entwurf", "Link or note on the draft")}
          <textarea value={text} maxLength={4000} onChange={(e) => setText(e.target.value)} placeholder={t("https://… oder kurze Beschreibung, Caption …", "https://… or a short description, caption …")} />
        </label>
        {error && <p className="tk-error" role="alert">{error}</p>}
        <div className="row">
          <button className="primary" type="submit" disabled={busy !== null || leer || laufend > 0}>{t("Entwurf einreichen", "Submit draft")}</button>
          {laufend > 0 && <small className="muted">{t("Erst wenn der Upload fertig ist.", "Once the upload has finished.")}</small>}
          <button type="button" onClick={onClose}>{t("Abbrechen", "Cancel")}</button>
        </div>
      </form>
    </Dialog>
  );
}

function LiveDialog({ beitrag, name, today, onClose }: { beitrag: CDeliverable; name: string; today: string; onClose: () => void }) {
  const t = useT();
  const lang = useLang();
  const { busy, error, run } = useCreatorAction();
  const [link, setLink] = useState(beitrag.live_url ?? "");
  const [datum, setDatum] = useState(beitrag.published_on ?? today);
  const linkFehler = link.trim() && !/^https?:\/\/\S+$/i.test(link.trim()) ? t("Der Link muss mit http:// oder https:// beginnen.", "The link must start with http:// or https://.") : null;
  const zukunft = datum > today;
  return (
    <Dialog open onClose={onClose} title={t(`Live melden · ${name}`, `Report live · ${name}`)}>
      <form className="stack" onSubmit={(e) => {
        e.preventDefault();
        if (!link.trim() || linkFehler || !datum || zukunft) return;
        void run("live", () => reportDeliverableLive(api, { deliverableId: beitrag.id, url: link, publishedOn: datum }),
          t("Danke! Deine Agentur prüft die Kennzeichnung.", "Thanks! Your agency will check the ad label.")).then((r) => r && onClose());
      }}>
        <p>{t("Füge den Link zum veröffentlichten Beitrag ein. Denk an die Kennzeichnung als Werbung (z. B. „Werbung“ oder „Anzeige“ am Anfang).",
          "Paste the link to the published post. Remember to label it as an ad (e.g. “Ad” or “Advertisement” at the start).")}</p>
        <label>{t("Link zum Beitrag", "Link to the post")}
          <input type="url" required value={link} placeholder="https://" onChange={(e) => setLink(e.target.value)} aria-invalid={!!linkFehler} />
          {linkFehler && <small className="tk-error">{linkFehler}</small>}
        </label>
        <label>{t("Veröffentlicht am", "Published on")}
          <input type="date" required max={today} value={datum} onChange={(e) => setDatum(e.target.value)} aria-invalid={zukunft} />
          {zukunft && <small className="tk-error">{t("Das Datum liegt in der Zukunft.", "The date is in the future.")}</small>}
        </label>
        {beitrag.usage_rights_months && (
          <p className="source-note">{t(`Die Marke darf den Beitrag ${beitrag.usage_rights_months} ${beitrag.usage_rights_months === 1 ? "Monat" : "Monate"} ab Veröffentlichung nutzen.`,
            `The brand may use the post for ${beitrag.usage_rights_months} ${beitrag.usage_rights_months === 1 ? "month" : "months"} from publication.`)}
            {usageRightsDetail(beitrag, lang) ? ` ${t("Umfang", "Scope")}: ${usageRightsDetail(beitrag, lang)}.` : ""}</p>
        )}
        {error && <p className="tk-error" role="alert">{error}</p>}
        <div className="row">
          <button className="primary" type="submit" disabled={busy !== null || !link.trim() || !!linkFehler || !datum || zukunft}>{t("Live melden", "Report live")}</button>
          <button type="button" onClick={onClose}>{t("Abbrechen", "Cancel")}</button>
        </div>
      </form>
    </Dialog>
  );
}
